import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UserSearch, Plus, Send, Copy, ShieldCheck, ShieldAlert, ShieldX,
  ClipboardList, CheckCircle2, XCircle, AlertCircle, FileText, Settings2,
} from "lucide-react";

const STATUS_META = {
  invited:        { label: "Invited",     tone: "border-white/15 text-zinc-300" },
  submitted:      { label: "Submitted",   tone: "border-sky-500/40 text-sky-300" },
  report_pending: { label: "Report Pending", tone: "border-amber-500/40 text-amber-300" },
  reviewed:       { label: "Reviewed",    tone: "border-indigo-500/40 text-indigo-300" },
  decided:        { label: "Decided",     tone: "border-emerald-500/40 text-emerald-300" },
};

const REC_META = {
  pass:   { icon: ShieldCheck, tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5", label: "Pass" },
  review: { icon: ShieldAlert, tone: "text-amber-300 border-amber-500/40 bg-amber-500/5", label: "Review" },
  fail:   { icon: ShieldX,     tone: "text-rose-300 border-rose-500/40 bg-rose-500/5", label: "Fail" },
};

export default function ScreeningPage() {
  const { currentProperty, propertyId } = useProperty();
  const pid = currentProperty?.id || propertyId;
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [criteria, setCriteria] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const params = { property_id: pid };
      if (statusFilter !== "all") params.status = statusFilter;
      const [r, c] = await Promise.all([
        api.get("/admin/screening", { params }),
        api.get(`/admin/screening/criteria/${pid}`).catch(() => ({ data: null })),
      ]);
      setItems(r.data.items || []);
      setCounts(r.data.counts_by_status || {});
      setCriteria(c.data?.criteria || null);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [pid, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="screening-page">
      <PageHeader
        eyebrow="Leasing"
        title="Renter Screening"
        subtitle="FCRA-compliant applicant screening with credit, criminal, eviction, and income verification."
        actions={
          <div className="flex flex-wrap gap-2">
            <CriteriaDialog propertyId={pid} criteria={criteria} onSaved={load} />
            <NewRequestDialog propertyId={pid} onCreated={load} />
          </div>
        }
      />

      {/* Status filters */}
      <div className="flex flex-wrap gap-2">
        <Chip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="screening-tab-all">
          All ({items.length})
        </Chip>
        {Object.keys(STATUS_META).map(s => (
          <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} testId={`screening-tab-${s}`}>
            {STATUS_META[s].label} ({counts[s] || 0})
          </Chip>
        ))}
      </div>

      {items.length === 0 && !loading && (
        <Card className="border-white/10 bg-card p-10 text-center" data-testid="screening-empty">
          <UserSearch className="h-8 w-8 mx-auto mb-3 text-zinc-500" />
          <div className="text-lg font-semibold">No screening requests yet</div>
          <div className="text-sm text-zinc-400 mt-2 mb-4">
            Kick off applicant screening &mdash; they&apos;ll get an FCRA-compliant consent link via email.
          </div>
          <NewRequestDialog propertyId={pid} onCreated={load} large />
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3">
        {items.map(r => (
          <RequestRow key={r.id} r={r} onOpen={() => setSelected(r.id)} onRefresh={load} />
        ))}
      </div>

      {selected && (
        <DetailDialog sid={selected} open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }} onRefresh={load} />
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
        (active ? "border-orange-400/60 bg-orange-500/10 text-orange-200"
                : "border-white/10 bg-white/[0.02] text-zinc-400 hover:text-zinc-200 hover:border-white/20")}
    >
      {children}
    </button>
  );
}

function RequestRow({ r, onOpen, onRefresh }) {
  const status = STATUS_META[r.status] || { label: r.status, tone: "border-white/15 text-zinc-300" };
  const rec = r.auto_recommendation ? REC_META[r.auto_recommendation] : null;
  const RecIcon = rec?.icon;
  const decision = r.decision?.decision;

  const copyLink = async () => {
    try {
      const { data: d } = await api.get(`/admin/screening/${r.id}`);
      if (d.consent_url) {
        await navigator.clipboard.writeText(d.consent_url);
        toast.success("Consent link copied");
      }
    } catch { toast.error("Couldn't fetch link"); }
  };

  const resend = async () => {
    try {
      await api.post(`/admin/screening/${r.id}/resend-invite`);
      toast.success("Reminder queued");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <Card className="border-white/10 bg-card p-4 hover:border-white/20 transition-colors" data-testid={`screening-row-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-semibold">{r.applicant_name}</div>
            <Badge variant="outline" className={status.tone}>{status.label}</Badge>
            {rec && (
              <Badge variant="outline" className={`gap-1 ${rec.tone}`}>
                <RecIcon className="h-3 w-3" /> Auto: {rec.label}
              </Badge>
            )}
            {decision && (
              <Badge variant="outline" className={decision === "approve" ? "border-emerald-500/40 text-emerald-300" : decision === "deny" ? "border-rose-500/40 text-rose-300" : "border-amber-500/40 text-amber-300"}>
                Decision: {decision}
              </Badge>
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {r.applicant_email}
            {r.unit && <> · Unit {r.unit}</>}
            {r.monthly_rent && <> · ${Number(r.monthly_rent).toLocaleString()}/mo</>}
            {r.provider && <> · via {r.provider}</>}
          </div>
          <div className="text-[11px] text-zinc-600 mt-1">Created {(r.created_at || "").slice(0, 10)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {r.status === "invited" && (
            <>
              <Button variant="outline" size="sm" onClick={copyLink} className="border-white/10 gap-1" data-testid={`screening-copy-${r.id}`}>
                <Copy className="h-3 w-3" /> Link
              </Button>
              <Button variant="outline" size="sm" onClick={resend} className="border-white/10 gap-1" data-testid={`screening-resend-${r.id}`}>
                <Send className="h-3 w-3" /> Resend
              </Button>
            </>
          )}
          <Button size="sm" onClick={onOpen} className="bg-orange-500 hover:bg-orange-600 gap-1" data-testid={`screening-open-${r.id}`}>
            <FileText className="h-3 w-3" /> Open
          </Button>
        </div>
      </div>
    </Card>
  );
}

function NewRequestDialog({ propertyId, onCreated, large = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    applicant_name: "", applicant_email: "", applicant_phone: "",
    monthly_rent: "", unit: "", provider: "manual", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    if (!form.applicant_name.trim() || !form.applicant_email.trim())
      return toast.error("Name and email required");
    setBusy(true);
    try {
      const body = { ...form, property_id: propertyId,
                     monthly_rent: form.monthly_rent ? Number(form.monthly_rent) : null };
      const { data: d } = await api.post("/admin/screening", body);
      toast.success("Screening invite sent");
      if (d.consent_url) {
        try { await navigator.clipboard.writeText(d.consent_url); toast.message("Consent link copied to clipboard"); } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/ScreeningPage.jsx: swallowed', err); }
      }
      setForm({ applicant_name: "", applicant_email: "", applicant_phone: "",
                 monthly_rent: "", unit: "", provider: "manual", notes: "" });
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={large ? "bg-orange-500 hover:bg-orange-600" : "bg-orange-500 hover:bg-orange-600 gap-2"}
                data-testid={large ? "screening-new-empty-btn" : "screening-new-btn"}>
          <Plus className="h-4 w-4 mr-2" /> New screening
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="screening-new-dialog">
        <DialogHeader>
          <DialogTitle>Invite applicant to screening</DialogTitle>
          <DialogDescription className="text-xs">
            Sends an FCRA-compliant consent email. Applicant fills out the secure form, then you enter or import the report results.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Applicant name</Label>
              <Input value={form.applicant_name}
                     onChange={(e) => setForm({ ...form, applicant_name: e.target.value })}
                     data-testid="screening-form-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.applicant_email}
                     onChange={(e) => setForm({ ...form, applicant_email: e.target.value })}
                     data-testid="screening-form-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.applicant_phone}
                     onChange={(e) => setForm({ ...form, applicant_phone: e.target.value })}
                     data-testid="screening-form-phone" />
            </div>
            <div>
              <Label>Unit</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                     placeholder="4B" data-testid="screening-form-unit" />
            </div>
            <div>
              <Label>Monthly rent</Label>
              <Input type="number" value={form.monthly_rent}
                     onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })}
                     placeholder="2500" data-testid="screening-form-rent" />
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger className="bg-white/[0.02] border-white/10" data-testid="screening-form-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual entry</SelectItem>
                  <SelectItem value="smartmove">TransUnion SmartMove</SelectItem>
                  <SelectItem value="rentprep">RentPrep</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Internal notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Referred by current resident 4A" data-testid="screening-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="screening-form-submit">
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ sid, open, onOpenChange, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportForm, setReportForm] = useState({
    credit_score: "", income_verified: "", criminal_hits: 0,
    eviction_hits: 0, judgments: 0, bankruptcies: 0,
    collections_amount: 0, provider: "manual", provider_ref: "", notes: "",
  });
  const [decisionForm, setDecisionForm] = useState({
    decision: "approve", reason: "", conditions: "",
  });

  const load = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/screening/${sid}`);
      setDetail(d);
      if (d.report) {
        setReportForm(prev => ({ ...prev, ...d.report }));
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [sid]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveReport = async () => {
    try {
      const body = {
        credit_score: reportForm.credit_score ? Number(reportForm.credit_score) : null,
        income_verified: reportForm.income_verified ? Number(reportForm.income_verified) : null,
        criminal_hits: Number(reportForm.criminal_hits) || 0,
        eviction_hits: Number(reportForm.eviction_hits) || 0,
        judgments: Number(reportForm.judgments) || 0,
        bankruptcies: Number(reportForm.bankruptcies) || 0,
        collections_amount: Number(reportForm.collections_amount) || 0,
        provider: reportForm.provider,
        provider_ref: reportForm.provider_ref,
        notes: reportForm.notes,
      };
      const { data: r } = await api.post(`/admin/screening/${sid}/report`, body);
      toast.success(`Report saved · Recommendation: ${r.recommendation}`);
      load();
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const saveDecision = async () => {
    try {
      await api.post(`/admin/screening/${sid}/decision`, decisionForm);
      toast.success(`Applicant ${decisionForm.decision}d`);
      load();
      onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (!detail && !loading) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="screening-detail-dialog">
        {(loading || !detail) && (
          <>
            <DialogHeader>
              <DialogTitle>Screening request</DialogTitle>
              <DialogDescription className="text-xs">Loading applicant record…</DialogDescription>
            </DialogHeader>
            <div className="text-sm text-zinc-500 py-10 text-center">Loading…</div>
          </>
        )}
        {detail && (
          <>
            <DialogHeader>
              <DialogTitle>{detail.applicant_name}</DialogTitle>
              <DialogDescription className="text-xs">
                {detail.applicant_email}{detail.unit ? ` · Unit ${detail.unit}` : ""}{detail.monthly_rent ? ` · $${Number(detail.monthly_rent).toLocaleString()}/mo` : ""}
              </DialogDescription>
            </DialogHeader>

            {/* SmartMove provider status */}
            {detail.provider === "smartmove" && (
              <Card className={`p-3 border ${detail.provider_status === "complete" ? "border-emerald-500/30 bg-emerald-500/[0.03]" : detail.provider_error ? "border-rose-500/30 bg-rose-500/[0.03]" : "border-sky-500/30 bg-sky-500/[0.03]"}`}
                    data-testid="screening-provider-status">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-sky-300">TransUnion SmartMove</div>
                    <div className="text-sm mt-1">
                      Status: <span className="font-mono">{detail.provider_status || "awaiting"}</span>
                      {detail.provider_request_id && <> · Ref: <span className="font-mono text-xs">{detail.provider_request_id}</span></>}
                    </div>
                    {detail.provider_error && <div className="text-xs text-rose-300 mt-1">Error: {detail.provider_error}</div>}
                    {!detail.provider_request_id && !detail.provider_error && (
                      <div className="text-xs text-zinc-400 mt-1">
                        SmartMove not enabled — internal manual flow will be used. Set SMARTMOVE_ENABLED=true + credentials in backend/.env to activate.
                      </div>
                    )}
                  </div>
                  {detail.provider === "smartmove" && (
                    <Button size="sm" variant="outline" className="border-sky-500/40 text-sky-300 gap-1 h-8 disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={!detail.provider_request_id}
                            data-testid="smartmove-pull-report"
                            onClick={async () => {
                              try {
                                const { data: r } = await api.post(`/admin/screening/${sid}/pull-report`);
                                if (r.ok) { toast.success(`Report pulled · ${r.recommendation}`); load(); onRefresh?.(); }
                                else toast.info(r.reason || "No report yet");
                              } catch (e) { toast.error(apiErrorMessage(e)); }
                            }}>
                      {detail.provider_request_id ? "Pull report" : "Pull report (needs SmartMove active)"}
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {/* Applicant consent block */}
            {detail.applicant_consent ? (
              <Card className="p-4 border-white/10 bg-white/[0.02]" data-testid="screening-detail-consent">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Applicant consent submitted</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="DOB" value={detail.applicant_consent.dob} />
                  <Field label="SSN" value={"···· ···· " + (detail.applicant_consent.ssn_last4 || "")} />
                  <Field label="Address" value={detail.applicant_consent.current_address} />
                  <Field label="Monthly income" value={detail.applicant_consent.monthly_income ? `$${Number(detail.applicant_consent.monthly_income).toLocaleString()}` : "—"} />
                  <Field label="Employer" value={detail.applicant_consent.employer || "—"} />
                  <Field label="Signed" value={(detail.applicant_consent.consent_signed_at || "").slice(0, 10)} />
                </div>
              </Card>
            ) : (
              <Card className="p-4 border-amber-500/30 bg-amber-500/[0.05] text-sm" data-testid="screening-detail-pending-consent">
                <div className="flex items-center gap-2 text-amber-300 font-medium">
                  <AlertCircle className="h-4 w-4" /> Awaiting applicant consent
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  Consent link: <span className="font-mono">{detail.consent_url}</span>
                </div>
              </Card>
            )}

            {/* Auto recommendation */}
            {detail.auto_recommendation && (
              <Card className={`p-4 border ${REC_META[detail.auto_recommendation]?.tone}`} data-testid="screening-detail-rec">
                <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Auto recommendation</div>
                <div className="text-2xl font-semibold font-display capitalize">{detail.auto_recommendation}</div>
                <div className="mt-2 space-y-1">
                  {(detail.checks || []).map((c) => (
                    <div key={c.label} className="text-xs flex items-start gap-2">
                      {c.ok
                        ? <CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-300 flex-shrink-0" />
                        : c.level === "fatal"
                          ? <XCircle className="h-3 w-3 mt-0.5 text-rose-300 flex-shrink-0" />
                          : <AlertCircle className="h-3 w-3 mt-0.5 text-amber-300 flex-shrink-0" />}
                      <span className={c.ok ? "text-zinc-400" : c.level === "fatal" ? "text-rose-300" : "text-amber-300"}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Report entry form */}
            {detail.status !== "decided" && (
              <div>
                <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" /> {detail.report ? "Update report" : "Enter report results"}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Credit score</Label>
                    <Input type="number" value={reportForm.credit_score}
                           onChange={(e) => setReportForm({ ...reportForm, credit_score: e.target.value })}
                           placeholder="720" data-testid="screening-report-credit" />
                  </div>
                  <div>
                    <Label>Verified income (mo)</Label>
                    <Input type="number" value={reportForm.income_verified}
                           onChange={(e) => setReportForm({ ...reportForm, income_verified: e.target.value })}
                           placeholder="8500" data-testid="screening-report-income" />
                  </div>
                  <div>
                    <Label>Eviction filings</Label>
                    <Input type="number" value={reportForm.eviction_hits}
                           onChange={(e) => setReportForm({ ...reportForm, eviction_hits: e.target.value })}
                           data-testid="screening-report-evictions" />
                  </div>
                  <div>
                    <Label>Criminal hits</Label>
                    <Input type="number" value={reportForm.criminal_hits}
                           onChange={(e) => setReportForm({ ...reportForm, criminal_hits: e.target.value })}
                           data-testid="screening-report-criminal" />
                  </div>
                  <div>
                    <Label>Collections ($)</Label>
                    <Input type="number" value={reportForm.collections_amount}
                           onChange={(e) => setReportForm({ ...reportForm, collections_amount: e.target.value })}
                           data-testid="screening-report-collections" />
                  </div>
                  <div>
                    <Label>Bankruptcies</Label>
                    <Input type="number" value={reportForm.bankruptcies}
                           onChange={(e) => setReportForm({ ...reportForm, bankruptcies: e.target.value })}
                           data-testid="screening-report-bk" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label>Provider</Label>
                    <Input value={reportForm.provider} onChange={(e) => setReportForm({ ...reportForm, provider: e.target.value })}
                           data-testid="screening-report-provider" />
                  </div>
                  <div>
                    <Label>Provider reference</Label>
                    <Input value={reportForm.provider_ref} onChange={(e) => setReportForm({ ...reportForm, provider_ref: e.target.value })}
                           placeholder="TU-000123456" data-testid="screening-report-ref" />
                  </div>
                </div>
                <Button onClick={saveReport} className="bg-orange-500 hover:bg-orange-600 mt-3" data-testid="screening-report-save">
                  Save report + Re-score
                </Button>
              </div>
            )}

            {/* Decision form */}
            {detail.report && detail.status !== "decided" && (
              <Card className="p-4 border-white/10 bg-white/[0.02]" data-testid="screening-decision-form">
                <div className="text-sm font-semibold mb-3">Issue decision</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Decision</Label>
                    <Select value={decisionForm.decision} onValueChange={(v) => setDecisionForm({ ...decisionForm, decision: v })}>
                      <SelectTrigger className="bg-white/[0.02] border-white/10" data-testid="screening-decision-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approve">Approve</SelectItem>
                        <SelectItem value="conditional">Conditional</SelectItem>
                        <SelectItem value="deny">Deny</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reason (adverse action ref)</Label>
                    <Input value={decisionForm.reason} onChange={(e) => setDecisionForm({ ...decisionForm, reason: e.target.value })}
                           placeholder="Credit score below threshold" data-testid="screening-decision-reason" />
                  </div>
                </div>
                {decisionForm.decision === "conditional" && (
                  <div className="mt-3">
                    <Label>Conditions</Label>
                    <Textarea rows={2} value={decisionForm.conditions}
                              onChange={(e) => setDecisionForm({ ...decisionForm, conditions: e.target.value })}
                              placeholder="Requires co-signer OR additional 1-month security deposit" data-testid="screening-decision-conditions" />
                  </div>
                )}
                <Button onClick={saveDecision} className="mt-3 bg-orange-500 hover:bg-orange-600" data-testid="screening-decision-save">
                  Save decision
                </Button>
              </Card>
            )}

            {/* Already-decided banner */}
            {detail.decision && (
              <Card className="p-4 border-emerald-500/30 bg-emerald-500/[0.05]" data-testid="screening-detail-decided">
                <div className="flex items-center gap-2 text-emerald-300 font-medium capitalize">
                  <CheckCircle2 className="h-4 w-4" />
                  {detail.decision.decision} · {(detail.decision.decided_at || "").slice(0, 10)}
                </div>
                {detail.decision.reason && <div className="text-xs mt-1 text-zinc-400">Reason: {detail.decision.reason}</div>}
                {detail.decision.conditions && <div className="text-xs mt-1 text-zinc-400">Conditions: {detail.decision.conditions}</div>}
              </Card>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm mt-0.5 font-mono">{value}</div>
    </div>
  );
}

function CriteriaDialog({ propertyId, criteria, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { if (open && criteria) setForm(criteria); }, [open, criteria]);

  const save = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    try {
      const body = { property_id: propertyId,
                     min_credit_score: Number(form.min_credit_score) || null,
                     min_income_multiplier: Number(form.min_income_multiplier) || null,
                     no_evictions_within_years: Number(form.no_evictions_within_years) || null,
                     no_felony_within_years: Number(form.no_felony_within_years) || null,
                     allow_bankruptcies_within_years: Number(form.allow_bankruptcies_within_years) || null,
                     max_collections_amount: Number(form.max_collections_amount) || null };
      await api.post("/admin/screening/criteria", body);
      toast.success("Screening criteria saved");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-white/10 gap-2" data-testid="screening-criteria-btn">
          <Settings2 className="h-4 w-4" /> Criteria
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-md" data-testid="screening-criteria-dialog">
        <DialogHeader>
          <DialogTitle>Screening criteria</DialogTitle>
          <DialogDescription className="text-xs">
            Drives the auto pass/review/fail recommendation. You always make the final decision.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Min credit score</Label>
            <Input type="number" value={form.min_credit_score || ""}
                   onChange={(e) => setForm({ ...form, min_credit_score: e.target.value })}
                   data-testid="crit-credit" />
          </div>
          <div>
            <Label>Income × rent</Label>
            <Input type="number" step="0.1" value={form.min_income_multiplier || ""}
                   onChange={(e) => setForm({ ...form, min_income_multiplier: e.target.value })}
                   data-testid="crit-income-mult" />
          </div>
          <div>
            <Label>Evictions look-back (yrs)</Label>
            <Input type="number" value={form.no_evictions_within_years || ""}
                   onChange={(e) => setForm({ ...form, no_evictions_within_years: e.target.value })}
                   data-testid="crit-evict-yrs" />
          </div>
          <div>
            <Label>Felony look-back (yrs)</Label>
            <Input type="number" value={form.no_felony_within_years || ""}
                   onChange={(e) => setForm({ ...form, no_felony_within_years: e.target.value })}
                   data-testid="crit-felony-yrs" />
          </div>
          <div>
            <Label>BK look-back (yrs)</Label>
            <Input type="number" value={form.allow_bankruptcies_within_years || ""}
                   onChange={(e) => setForm({ ...form, allow_bankruptcies_within_years: e.target.value })}
                   data-testid="crit-bk-yrs" />
          </div>
          <div>
            <Label>Max collections ($)</Label>
            <Input type="number" value={form.max_collections_amount || ""}
                   onChange={(e) => setForm({ ...form, max_collections_amount: e.target.value })}
                   data-testid="crit-collections" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="crit-save">Save criteria</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
